import { Icon, Stack } from '@mui/material';
import { Product, ProductBillingType, ProductTypeId, SchoolYear } from '@schooly/api';
import { useFlag } from '@schooly/hooks/use-flag';
import { ArrowDownV2Icon, Counter, TypographyWithOverflowHint } from '@schooly/style';
import { FC, useMemo } from 'react';

import { DropdownSelectExpandableRow } from '../DropdownSelectExpandableRow';
import { DropdownSelectRow } from '../DropdownSelectRow';
import { getLatestProductTypes } from './getLatestProductTypes';

type ProductSelectRowProps = {
  isSelected: boolean;
  onSelectProduct: (id: string) => void;
  onSelectProductType: (id: ProductTypeId) => void;
  product: Product;
  selectedProductTypes: ProductTypeId[];
  schoolYear?: SchoolYear;
};

export const ProductSelectRow: FC<ProductSelectRowProps> = ({
  isSelected,
  product,
  selectedProductTypes,
  onSelectProduct,
  onSelectProductType,
  schoolYear,
}) => {
  const [isExpanded, expand, collapse] = useFlag(false);
  const isRecurring = product.type === ProductBillingType.Recurring;

  const { activeTypes } = useMemo(() => {
    const types = isRecurring
      ? product.types
      : getLatestProductTypes(product.types, schoolYear?.end);

    return { activeTypes: types };
  }, [isRecurring, product.types, schoolYear?.end]);

  const renderOption = (productType: Product['types'][0]) => {
    const isOptionSelected =
      isSelected || selectedProductTypes.some((pt) => pt.typeId === productType.id);

    return (
      <DropdownSelectRow
        key={productType.id}
        isSelected={isOptionSelected}
        onClick={() => onSelectProductType(new ProductTypeId(productType.id, product.id))}
        sx={(theme) => ({
          '&:hover': {
            backgroundColor: theme.palette.background.default,
            '& .MuiTypography-root': {
              color: theme.palette.primary.main,
            },
          },
        })}
      >
        <Stack direction="row" gap={1} flex={1}>
          <TypographyWithOverflowHint
            color={!isOptionSelected ? 'common.grey2' : undefined}
            variant="h3"
            noWrap
          >
            {productType.name}
          </TypographyWithOverflowHint>
        </Stack>
      </DropdownSelectRow>
    );
  };

  const isExpandable = activeTypes.length > 1;

  const rowContent = (
    <Stack flexDirection="row" alignItems="center" gap={0.5}>
      <TypographyWithOverflowHint
        color={!isSelected ? 'common.grey2' : undefined}
        variant="h3"
        noWrap
      >
        {product.name}
      </TypographyWithOverflowHint>
      {isExpandable && (
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1}
          sx={{
            cursor: 'pointer',
            transition: 'all .2s',
            '&:hover': { opacity: 0.5 },
            color: isSelected ? 'primary.main' : undefined,
          }}
          onClick={(e) => {
            e.stopPropagation();
            isExpanded ? collapse() : expand();
          }}
        >
          <Counter
            className="counter"
            inverse
            sx={(theme) => ({
              minWidth: 20,
              textAlign: 'center',
              marginLeft: 0,
              padding: theme.spacing(0, 0.5),
              background: 'transparent',
              borderColor: isSelected ? theme.palette.primary.main : theme.palette.common.grey2,
              color: isSelected ? theme.palette.primary.main : theme.palette.common.grey2,
            })}
          >
            {selectedProductTypes.length ? `${selectedProductTypes.length}/` : ''}
            {activeTypes.length}
          </Counter>
          <Icon
            className="arrowIcon"
            sx={(theme) => ({
              width: theme.spacing(1),
              transform: isExpanded ? 'rotate(180deg)' : undefined,
              path: {
                stroke: isExpanded ? theme.palette.primary.main : theme.palette.common.grey2,
              },
            })}
          >
            <ArrowDownV2Icon />
          </Icon>
        </Stack>
      )}
    </Stack>
  );

  const expandedContent = isExpanded ? (
    <Stack gap={1} mt={1}>
      {activeTypes.map(renderOption)}
    </Stack>
  ) : undefined;

  return (
    <DropdownSelectExpandableRow
      key={product.id}
      onClick={() => onSelectProduct(product.id)}
      isSelected={isSelected}
      expandedContent={expandedContent}
      sx={(theme) => ({
        '&:hover': {
          backgroundColor: (theme) => theme.palette.background.default,
          '& .MuiTypography-root': {
            color: 'primary.main',
          },
          '& .counter': {
            borderColor: 'primary.main',
          },
          '& .arrowIcon': {
            path: {
              stroke: (theme) =>
                isExpanded ? theme.palette.primary.main : theme.palette.common.grey2,
            },
          },
        },
      })}
    >
      {rowContent}
    </DropdownSelectExpandableRow>
  );
};
